import { useEffect, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import { tripQueryOptions } from "@/app/lib/hooks/useTrips";
import { OverviewCard } from "@/app/ui/trips/bookingFlow/tripDetails/OverviewCard";
import { TripTourDrawer } from "@/app/ui/trips/bookingFlow/tripDetails/TripTourDrawer";
import {
  InclusionsCard,
  ExclusionsCard,
} from "@/app/ui/trips/bookingFlow/tripDetails/FeaturesCards";
import { VisaPermissionsCard } from "@/app/ui/trips/bookingFlow/tripDetails/VisaPermissionsCard";
import { HotelsCard } from "@/app/ui/trips/bookingFlow/tripDetails/HotelsCard";
import { AdditionalInformationCard } from "@/app/ui/trips/bookingFlow/tripDetails/AdditionalInformationCard";
import { TermsAndConditionsCard } from "@/app/ui/trips/bookingFlow/tripDetails/TermsAndConditionsCard";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { formatCurrency } from "@/app/lib/utils/currency";
import type { BookingData } from "@/app/ui/trips/types";
import { TripTourPlanCard } from "@/app/ui/trips/bookingFlow/tripDetails/TripTourPlanCard";
import { TripTourCard } from "@/app/ui/trips/bookingFlow/tripDetails/TripTourCard";
import currency from "currency.js";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";

export const Route = createFileRoute("/_authenticated/trips/$tripId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();
  const { setValue } = useFormContext<BookingData>();

  const [isTripTourDrawerOpen, setTripTourDrawerOpen] = useState(false);

  const isTripTourAdded = useWatch<BookingData, "isTripTourAdded">({
    name: "isTripTourAdded",
  });

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_DETAILS, {
      trip_id: tripId,
      trip_name: data.name,
    });
  }, [tripId, data.name]);

  const handleContinue = () => {
    if (
      isFeatureEnabled(Feature.OptionalTours) &&
      data.hasOptionalTours &&
      !isTripTourAdded
    ) {
      rudderAnalytics.track(
        TripTrackingEvents.USER_VIEWED_TRIP_OPTIONAL_TOURS,
        {
          source: "auto",
          trip_id: data.id,
          trip_name: data.name,
          price: data.tourPrice ?? 0,
        },
      );

      setTripTourDrawerOpen(true);
      return;
    }

    proceedToBooking();
  };

  const proceedToBooking = () => {
    rudderAnalytics.track(
      TripTrackingEvents.USER_PROCEEDED_TO_TRIP_AVAILABILITY,
      {
        trip_id: tripId,
        trip_name: data.name,
      },
    );
    updatePurchaseState("inProgress");
    navigate({ to: "./booking" });
  };

  return (
    <>
      <div className="space-y-3">
        <OverviewCard trip={data} />

        {isFeatureEnabled(Feature.OptionalTours) && data.hasOptionalTours ? (
          <TripTourCard trip={data} source="details" />
        ) : data.itineraries.length > 0 ? (
          <TripTourPlanCard itineraries={data.itineraries} />
        ) : null}

        <InclusionsCard trip={data} />
        <ExclusionsCard trip={data} />
        <HotelsCard trip={data} />
        <VisaPermissionsCard countries={data.countries} />
        {data.additionalInformation ? (
          <AdditionalInformationCard content={data.additionalInformation} />
        ) : null}
        {data.termsAndConditions ? (
          <TermsAndConditionsCard content={data.termsAndConditions} />
        ) : null}
      </div>

      <PersistentDrawer>
        <dl className="mt-2 mb-4 grid grid-cols-2 gap-2 text-sm text-text-primary">
          <dt>{t("trips.totalPriceFrom")}</dt>
          <dd className="text-end font-bold">
            {formatCurrency(
              currency(data.discountedPrice).add(
                isTripTourAdded ? (data.tourPrice ?? 0) : 0,
              ).value,
            )}
          </dd>
        </dl>

        <Button fullWidth variant="contained" onClick={handleContinue}>
          {t("trips.continue")}
        </Button>
      </PersistentDrawer>

      {isFeatureEnabled(Feature.OptionalTours) &&
        data.hasOptionalTours &&
        !isTripTourAdded && (
          <TripTourDrawer
            trip={data}
            open={isTripTourDrawerOpen}
            onClose={() => setTripTourDrawerOpen(false)}
          >
            <Button
              sx={{ width: "45%" }}
              variant="outlined"
              onClick={proceedToBooking}
            >
              {t("trips.tours.drawer.skip")}
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                rudderAnalytics.track(
                  TripTrackingEvents.USER_ADDED_TRIP_OPTIONAL_TOURS,
                  {
                    source: "auto",
                    trip_id: data.id,
                    trip_name: data.name,
                    price: data.tourPrice ?? 0,
                  },
                );

                setValue("isTripTourAdded", true);
                proceedToBooking();
              }}
            >
              {t("trips.tours.drawer.addTours")}
            </Button>
          </TripTourDrawer>
        )}
    </>
  );
}
